import { useContext, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  useAiTagMappingsApi,
  useCreateAiTagMutation,
  useDeleteAiTagMutation,
  useUpdateAiTagMutation
} from '@/common/api/modules/aiTag'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { aiTagCategoryLabel } from '@/common/app/modules/recommend/aiTag'
import { aiTagEditPath, aiTagsPath } from '@/common/app/path'
import { useShowDeletedToast, useShowSavedToast } from '@/common/app/toast'
import { registerNumberOption } from '@/common/form/register'
import { AITagCategory, AITagMaster } from '@/common/model/recommend/aiTag'
import { preventKeyDown } from '@/common/util/keyboard'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import SelectButton from '@/components/view/atoms/button/SelectButton'
import SortButton from '@/components/view/atoms/button/SortButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import SimpleTable from '@/components/view/atoms/table/SimpleTable'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

import {
  IMPORTANCE_CHOICES,
  TAG_FORM_DEFAULT,
  TagFormSchemaType,
  convertSchemaToCreateVars,
  convertSchemaToUpdateVars,
  convertTagToSchema,
  tagNameLabelMapping,
  tagSchema
} from './tagSchema'

const REFERENCE_TABS = [
  { key: 'all', label: 'すべて' },
  { key: 'product', label: '商品' },
  { key: 'feature', label: '特集記事' }
] as const

type ReferenceTab = (typeof REFERENCE_TABS)[number]['key']

const TAG_CATEGORY_CHOICES = Object.values(AITagCategory).map((value) => ({
  name: aiTagCategoryLabel(value),
  value
}))

interface Props {
  siteId: number
  tag?: AITagMaster
  onRefetch?: () => void
}

const TagForm = ({ siteId, tag, onRefetch }: Props) => {
  const navigate = useNavigate()
  const [referenceTab, setReferenceTab] = useState<ReferenceTab>('all')

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<TagFormSchemaType>({
    resolver: zodResolver(tagSchema),
    values: tag ? convertTagToSchema(tag) : TAG_FORM_DEFAULT
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, tagNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!tag })
  const showDeleted = useShowDeletedToast()
  const { showModal: showConfirm } = useContext(ConfirmDialogContext)

  const { data: mappingsData } = useAiTagMappingsApi(
    tag ? { site_id: siteId, tag_id: tag.id } : null
  )

  const { trigger: createTag } = useCreateAiTagMutation({
    onSuccess: (result) => {
      showSaved('AIタグ')
      navigate(aiTagEditPath(siteId, result.id))
    }
  })

  const { trigger: updateTag } = useUpdateAiTagMutation({
    onSuccess: () => {
      showSaved('AIタグ')
      onRefetch?.()
    }
  })

  const { trigger: deleteTag } = useDeleteAiTagMutation()

  const onSubmit: SubmitHandler<TagFormSchemaType> = (data) => {
    if (tag) {
      updateTag(convertSchemaToUpdateVars(data, siteId, tag.id))
    } else {
      createTag(convertSchemaToCreateVars(data, siteId))
    }
  }

  const handleDelete = () => {
    if (!tag) return
    showConfirm({
      message: `タグ「${tag.tag_name}」を削除しますか？この操作は元に戻せません`,
      yesText: '削除する',
      noText: 'キャンセル',
      onClickYes: async () => {
        await deleteTag({ site_id: siteId, tag_id: tag.id })
        showDeleted('AIタグ')
        navigate(aiTagsPath(siteId))
      }
    })
  }

  const allMappings = mappingsData?.mappings ?? []
  const filteredMappings =
    referenceTab === 'product'
      ? allMappings.filter((m) => m.source_type === 'w2_Product')
      : referenceTab === 'feature'
        ? allMappings.filter((m) => m.source_type === 'w2_FeaturePageContents')
        : allMappings

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={tag ? tag.tag_name : 'タグ新規作成'}
        badgeNode={<AppTitleBadge type='recommend' />}
      />
      {isDirty && (
        <TopBar
          status={tag ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={tag ? '未反映の変更があります' : '登録されていない新しいタグ'}
          onCancel={() => reset(tag ? convertTagToSchema(tag) : TAG_FORM_DEFAULT)}
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>タグ情報</Heading>
          <AreaContent>
            <FormRow direction='vertical' gap={2}>
              <FormLabel required id='tag_name'>
                タグ名
              </FormLabel>
              <FormTextField
                register={register('tag_name')}
                fieldType={FieldType.REQUIRED}
                error={!!errors.tag_name?.message}
                size={TextBoxSize.LARGE}
                placeholder='タグ名'
              />
              <FormError message={errors.tag_name?.message} />
            </FormRow>
            <FormRow direction='vertical' gap={2}>
              <FormLabel id='tag_category'>タグカテゴリ</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormSelector
                  choices={TAG_CATEGORY_CHOICES}
                  register={register('tag_category')}
                  error={!!errors.tag_category?.message}
                />
                <FormError message={errors.tag_category?.message} />
              </FormRow>
            </FormRow>
            <FormRow direction='vertical' gap={2}>
              <FormLabel id='importance_score'>重要度</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormSelector
                  choices={[...IMPORTANCE_CHOICES]}
                  register={register('importance_score', registerNumberOption)}
                  error={!!errors.importance_score?.message}
                />
                <FormError message={errors.importance_score?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        {tag && (
          <Area noBorder>
            <Heading>リファレンス</Heading>
            <AreaContent>
              <FormRow align='center' size={TextBoxSize.FULL}>
                <ItemAlign gapX={4} nowrap>
                  <Text weight='bold' nowrap>
                    出現数
                  </Text>
                  <Text size={14}>{tag.occurrence_count}</Text>
                </ItemAlign>
                <InsideArea color={BgColor.SECONDARY}>
                  <ItemAlign gapX={16}>
                    <Text>{tag.product_count}商品</Text>
                    <Text>{tag.feature_count}件の記事</Text>
                  </ItemAlign>
                </InsideArea>
              </FormRow>
              <FormRow>
                {REFERENCE_TABS.map((t) => (
                  <SortButton
                    key={t.key}
                    onClick={() => setReferenceTab(t.key)}
                    selected={referenceTab === t.key}
                    noPulldown
                  >
                    {t.label}
                  </SortButton>
                ))}
              </FormRow>
              {filteredMappings.length === 0 ? (
                <div className='py-8 text-center'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    紐づくデータがありません
                  </Text>
                </div>
              ) : (
                <SimpleTable>
                  <thead>
                    <TableRow head noBorder>
                      <TableHeadItem type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          ID
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          種別
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          名称
                        </Text>
                      </TableHeadItem>
                      <TableHeadItem type='thin'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          確度
                        </Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {filteredMappings.map((m) => (
                      <TableRow key={m.id}>
                        <TableRowItem type='middle'>
                          <Text size={12}>{m.source_id}</Text>
                        </TableRowItem>
                        <TableRowItem type='middle'>
                          <Text size={12}>
                            {m.source_type === 'w2_Product' ? '商品' : '特集記事'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem type='middle'>
                          <Text size={12}>{m.source_name || m.source_id}</Text>
                        </TableRowItem>
                        <TableRowItem type='middle'>
                          <Text size={12}>{(m.confidence_score * 100).toFixed(0)}%</Text>
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </SimpleTable>
              )}
            </AreaContent>
          </Area>
        )}
      </Content>

      <PageBottom between={!!tag}>
        {tag && (
          <Button type='button' color={ButtonColor.CRITICAL} size='large' onClick={handleDelete}>
            このタグを削除する
          </Button>
        )}
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {tag ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default TagForm
